import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { logout } from '../../store/slices/authSlice';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const UserProfile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  // Ensure user properties are properly typed
  const userWithPhoto = user as { firstName: string; lastName: string; mobileNumber: string; photoId?: string };
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleMenuClose();
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="body1" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
        Hi, {user.name}
      </Typography>
      
      <IconButton
        onClick={handleMenuOpen}
        size="small"
        sx={{ ml: 1 }}
        aria-controls="user-menu"
        aria-haspopup="true"
      >
        {userWithPhoto.photoId ? (
          <Avatar 
            alt={`${userWithPhoto.firstName} ${userWithPhoto.lastName}`} 
            src={`/api/images/${userWithPhoto.photoId}`}
            sx={{ width: 32, height: 32 }}
          />
        ) : (
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            <AccountCircleIcon />
          </Avatar>
        )}
      </IconButton>
      
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: '#252526',
            color: 'white',
          },
        }}
      >
        <MenuItem onClick={handleMenuClose}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 8 }}>
              <svg width="20" height="20" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </span>
            <Typography variant="body2">
              {user.role}
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem disableRipple sx={{ cursor: 'default' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 8 }}>
              <svg width="20" height="20" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z"/>
              </svg>
            </span>
            <Typography variant="body2">
              {userWithPhoto.mobileNumber}
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 8 }}>
              <svg width="20" height="20" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                <path d="M16 13v-2H7V9h9V7l5 4-5 4zm-2 5H5V4h9V2H5a2 2 0 00-2 2v16a2 2 0 002 2h9v-2z"/>
              </svg>
            </span>
            <Typography variant="body2">Logout</Typography>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserProfile;
